/**
 * Audio- and text-track helpers around the Video.js track lists.
 *
 * These wrap the (lightly-typed) Video.js track APIs in a small, typed
 * surface used by the player UI and the sync engine. Importers must never
 * reach into `player.audioTracks()` / `player.textTracks()` directly.
 */
import type { Player } from './useVideoJs';

/** A player audio track surfaced for UI consumption. */
export interface PlayerAudioTrack {
  /** Video.js track id (coerced to string for stable comparison). */
  id: string;
  label: string;
  language: string;
  /** e.g. 'main' | 'alternative' | 'commentary' | 'descriptions'. */
  kind: string;
  /** Whether this track is currently the active audio rendition. */
  enabled: boolean;
}

/** A player text track surfaced for UI consumption. */
export interface PlayerTextTrack {
  /** Video.js track id (coerced to string for stable comparison). */
  id: string;
  label: string;
  language: string;
  /** e.g. 'subtitles' | 'captions' | 'descriptions' | 'chapters'. */
  kind: string;
  /** Current display mode. */
  mode: TextTrackMode;
}

/** Display modes for a Video.js / HTML text track. */
export type TextTrackMode = 'disabled' | 'hidden' | 'showing';

/** Minimal shape of a Video.js audio track we read/mutate. */
interface AudioTrackLike {
  readonly id: string | number;
  readonly label: string;
  readonly language: string;
  readonly kind: string;
  enabled: boolean;
}

/** Minimal shape of a Video.js text track we read/mutate. */
interface TextTrackLike {
  readonly id: string | number;
  readonly label: string;
  readonly language: string;
  readonly kind: string;
  mode: TextTrackMode;
}

/** Minimal iterable-ish track list shape (works for audio & text lists). */
interface TrackListLike<T> {
  readonly length: number;
  [index: number]: T;
  on(event: 'change', handler: () => void): void;
  off(event: 'change', handler: () => void): void;
}

/** Coerce a possibly-numeric Video.js track id to a stable string. */
function trackId(id: string | number): string {
  return String(id);
}

/** Convert a Video.js track list to a plain array of typed tracks. */
function trackListToArray<T extends AudioTrackLike | TextTrackLike>(
  list: TrackListLike<T>,
): T[] {
  const out: T[] = [];
  for (let i = 0; i < list.length; i++) {
    // WHY: noUncheckedIndexedAccess makes `list[i]` `T | undefined`; the
    // length loop guarantees presence so a non-null assertion is safe here.
    const track = list[i];
    if (track) out.push(track);
  }
  return out;
}

/**
 * List the player's audio tracks in declaration order.
 *
 * Works because the source is HLS — VHS exposes alternate audio renditions
 * as switchable audio tracks; MP4 audio tracks are not switchable in
 * Chrome/Firefox. @see Video.js audioTracks — mid-playback switch, no reload.
 */
export function listAudioTracks(player: Player): PlayerAudioTrack[] {
  const list = player.audioTracks() as unknown as TrackListLike<AudioTrackLike>;
  return trackListToArray(list).map((t) => ({
    id: trackId(t.id),
    label: t.label,
    language: t.language,
    kind: t.kind,
    enabled: t.enabled,
  }));
}

/**
 * Enable the audio track with `id` and disable all others.
 *
 * VHS performs the mid-playback rendition switch; there is no reload. If no
 * track matches, the call is a no-op (the current selection is left intact).
 */
export function selectAudioTrack(player: Player, id: string): void {
  const list = player.audioTracks() as unknown as TrackListLike<AudioTrackLike>;
  const tracks = trackListToArray(list);
  // WHY: only mutate when a matching track exists. Disabling every track on
  // a stale id would mute the video; track lists can change asynchronously
  // as VHS parses EXT-X-MEDIA, so a no-op on unknown id is the safe choice.
  if (!tracks.some((t) => trackId(t.id) === id)) return;
  for (const t of tracks) {
    t.enabled = trackId(t.id) === id;
  }
}

/**
 * List the player's text tracks in declaration order.
 *
 * Only one subtitles/captions track shows at a time (browser constraint).
 */
export function listTextTracks(player: Player): PlayerTextTrack[] {
  const list = player.textTracks() as unknown as TrackListLike<TextTrackLike>;
  return trackListToArray(list).map((t) => ({
    id: trackId(t.id),
    label: t.label,
    language: t.language,
    kind: t.kind,
    mode: t.mode,
  }));
}

/**
 * Select a single subtitles/captions track by id, or disable all of them
 * when `id` is null.
 *
 * Switching is done by toggling `mode` on the existing remote tracks rather
 * than removing/re-adding them: set the target to `'showing'` and every other
 * subtitles/captions track to `'disabled'`. This avoids a reload and keeps
 * the active cue state.
 *
 * WHY: some HLS setups do not honour a mode flip for a track that has never
 * been enabled (the rendition is not yet attached). In that rare case the
 * fallback is to remove the remote track and re-add it with a cache-busted
 * URL — see {@link reAddTextTrackWithCacheBust}. We do not do that here
 * because mode toggling is correct for FFmpeg-generated segmented WebVTT
 * renditions (the only kind this pipeline produces).
 *
 * @param player The Video.js player.
 * @param id     The target track id, or `null` to turn subtitles off.
 */
export function selectTextTrack(player: Player, id: string | null): void {
  const list = player.textTracks() as unknown as TrackListLike<TextTrackLike>;
  const tracks = trackListToArray(list);
  for (const t of tracks) {
    if (t.kind !== 'subtitles' && t.kind !== 'captions') continue;
    if (id !== null && trackId(t.id) === id) {
      t.mode = 'showing';
    } else {
      t.mode = 'disabled';
    }
  }
}

/**
 * Re-add a remote text track with a cache-busting query parameter.
 *
 * Fallback for the rare HLS setup where toggling `mode` does not switch the
 * active rendition (see {@link selectTextTrack}). The track is removed via
 * `removeRemoteTextTrack` and re-added with `<url>?_=<ts>` so the browser
 * fetches a fresh segment playlist. Not used on the default path.
 *
 * @see Video.js remote text tracks — addRemoteTextTrack / removeRemoteTextTrack.
 */
export function reAddTextTrackWithCacheBust(
  player: Player,
  track: { id: string; src: string; kind: 'subtitles' | 'captions'; label?: string; language?: string },
): void {
  const list = player.textTracks() as unknown as TrackListLike<TextTrackLike>;
  const tracks = trackListToArray(list);
  const existing = tracks.find((t) => trackId(t.id) === track.id);
  if (existing) {
    // removeRemoteTextTrack accepts the track object returned by addRemoteTextTrack.
    (player as unknown as {
      removeRemoteTextTrack(t: { track: TextTrackLike }): void;
    }).removeRemoteTextTrack({ track: existing });
  }
  const cacheBust = `${track.src}${track.src.includes('?') ? '&' : '?'}_=${Date.now()}`;
  // WHY: exactOptionalPropertyTypes forbids passing `undefined` to an
  // optional field, so build the opts object conditionally.
  const opts: { kind: string; src: string; label?: string; language?: string } = {
    kind: track.kind,
    src: cacheBust,
  };
  if (track.label !== undefined) opts.label = track.label;
  if (track.language !== undefined) opts.language = track.language;
  (player as unknown as {
    addRemoteTextTrack(o: typeof opts): { track: TextTrackLike };
  }).addRemoteTextTrack(opts);
}

/**
 * Subscribe to audio-track-list `change` events (fires when the enabled
 * rendition changes, including VHS-driven ABR-style audio switches).
 *
 * @returns An unsubscribe function that removes the listener.
 */
export function onAudioTracksChange(
  player: Player,
  handler: () => void,
): () => void {
  const list = player.audioTracks() as unknown as TrackListLike<AudioTrackLike>;
  list.on('change', handler);
  return () => list.off('change', handler);
}

/**
 * Subscribe to text-track-list `change` events (fires when a track's
 * `mode` changes, e.g. the user or {@link selectTextTrack} switches subs).
 *
 * @returns An unsubscribe function that removes the listener.
 */
export function onTextTracksChange(
  player: Player,
  handler: () => void,
): () => void {
  const list = player.textTracks() as unknown as TrackListLike<TextTrackLike>;
  list.on('change', handler);
  return () => list.off('change', handler);
}
